import React, { Fragment, useEffect, useLayoutEffect, useState } from 'react'
import RefinancingButton from '../RefinancingButton/RefinancingButton'
import { Button, Divider, Tooltip } from 'antd'
import format from 'utils/format'
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import SimulatorLine from 'egi/app/Simulator/components/SimulatorLine/SimulatorLine'
import translate from 'utils/translate'
import Modal from 'components/Modal/Modal'
import SimulatorResume from 'egi/app/Simulator/components/SimulatorResume/SimulatorResume'
import { useClient, useSimulation } from 'hooks'
import { useDispatch } from 'react-redux'
import { resetAllSimulation, simulationSteps } from 'store/modules/simulation/actions'
import { createProposal } from 'store/modules/proposal/actions'
import swal from 'utils/swal'

interface IHandleFinishSimulation {
  simulationId: string
  productId: string
  clientId?: string
  callback?: (error?: any) => void
}

export default function Simulation ({ callbackFunction }: { callbackFunction?: (proposalId: string) => void }) {
  const simulation = useSimulation()
  const dispatch = useDispatch()
  const client = useClient()
  const [modalTitle, setModalTitle] = useState<string>('INFORMAÇÕES SOBRE SUA SIMULAÇÃO')
  const [visible, setVisible] = useState<boolean>(false)
  const [indexes, setIndexes] = useState<boolean>(false)
  const [flatRate, setFlatRate] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState(false)

  useLayoutEffect(() => {
    if (simulation.lastStep?.possibleIndexes) {
      const getFlateRate = simulation.lastStep?.possibleIndexes.find((item: string) => item === 'FLATRATE')
      if (getFlateRate) {
        setIndexes(true)
      }
    }
  }, [simulation])

  useEffect(() => {
    if (simulation?.lastStep?.index === 'FLATRATE') setFlatRate(true)
  }, [simulation.lastStep?.index])

  function closeDrawer () {
    setVisible(false)
    setModalTitle('INFORMAÇÕES SOBRE SUA SIMULAÇÃO')
  }

  function handleFinishSimulation ({ simulationId, productId, clientId, callback }: IHandleFinishSimulation) {
    dispatch(createProposal({ productId, simulationId, clientId, isRefinancing: true }, (id: string, error) => {
      if (callback) callback(error)

      if (callbackFunction) {
        callbackFunction(id)
        dispatch(simulationSteps(1))
        dispatch(resetAllSimulation())
      }
    }))
  }

  const onFinish = () => {
    setSubmitLoading(true)

    handleFinishSimulation({
      productId: String(simulation.values?.productId),
      simulationId: simulation.id || simulation.values?._id || '',
      clientId: client._id,
      callback: (error) => {
        if (error) {
          swal.basic({ title: 'Atenção', text: error.message, icon: 'warning' })
          setSubmitLoading(false)
        }
      }
    })
  }

  return (
    <Fragment>
      <Modal
        className="simulator-modal"
        width='80vw'
        title={<h1 className="simulator-title text-center my-3" style={{ lineHeight: 1.3 }}>{modalTitle}</h1>}
        centered
        visible={visible}
        footer={null}
        onOk={closeDrawer}
        onCancel={closeDrawer}
      >
        <SimulatorResume setModalTitle={setModalTitle} />
      </Modal>

      <div className='refinancing-content'>
        <div className="mb-3">
          <SimulatorLine
            label="Valor do imóvel"
            value={format.formatBRL(simulation.values?.propertyValue)}
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Valor do empréstimo"
            value={format.formatBRL(simulation.lastStep?.financing)}
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Saldo devedor"
            value={format.formatBRL(simulation?.values?.outstandingBalance)}
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="IOF"
            value={format.formatBRL(simulation?.values?.iof)}
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Avaliação"
            value={format.formatBRL(simulation?.values?.avaliationFare)}
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Custas cartorárias"
            value={format.formatBRL(simulation?.lastStep?.registerValue)}
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Valor do crédito líquido"
            value={format.formatBRL(simulation.lastStep?.liquid)}
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Valor da primeira parcela"
            value={simulation.lastStep?.parcels && simulation.lastStep?.parcels?.length > 0
              ? <label className="simulator-title" style={{ fontSize: 18 }}>{format.formatBRL(simulation.lastStep?.parcels[0].totalParcel)}</label>
              : (<LoadingOutlined />)}
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Prazo"
            value={simulation.values?.deadline + ' meses'}
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Tipo do imóvel"
            value={translate.subProducts(String(simulation.values?.sub))}
          />
        </div>

        <Divider />

        <div className="mb-3 text-center">
          <Button
            style={{ minWidth: 140 }}
            className={`mr-2  mb-md-2 mr-md-0 ${!flatRate ? 'simulator-calculation-radio-button' : 'simulator-specialist-radio-button'}`}
            type={flatRate ? 'ghost' : 'default'}
            onClick={() => {
              setFlatRate(false)
            }}
          >
            IPCA
          </Button>

          <Button
            style={{ minWidth: 140 }}
            className={`ml-md-3 ${flatRate ? 'simulator-calculation-radio-button' : 'simulator-specialist-radio-button'}`}
            type={flatRate ? 'default' : 'ghost'}
            disabled={(Number(simulation.lastStep?.deadline) <= 60 && indexes === true) ? false : true}
            onClick={() => {
              setFlatRate(true)
            }}
          >
            PRÉ-FIXADA
          </Button>

          <label className="pl-1">
            <Tooltip
              color="white"
              placement="right"
              title={<label className="color-black">A taxa pré-fixada é válida apenas para simulações de até 60 meses.</label>}
              destroyTooltipOnHide={{ keepParent: false }}
            >
              <InfoCircleOutlined className="simulation-info" style={{ color: '#00441F' }} />
            </Tooltip>
          </label>
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Sistema de amortização"
            value={
              <label style={{ textTransform: 'capitalize' }}>
                {simulation.lastStep?.amortizationType}
              </label>
            }
          />
        </div>

        <div className="mb-3">
          <SimulatorLine
            label="Taxa de juros"
            value={
              <Fragment>
                <label>{format.decimalToPercentage(Number(simulation.lastStep?.taxes?.monthlyTax)).toFixed(2)}%</label>
                <label>{flatRate ? ' ao mês + PRÉ-FIXADA' : ' ao mês + IPCA'}</label>
              </Fragment>
            }
            tooltip={
              <label className="pl-1">
                <Tooltip
                  color="white"
                  placement="right"
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    <p className="simulator-tooltip-paragraph mb-0">
                      <b>O IPCA (Índice Nacional de Preços ao Consumidor Amplo)</b> é um índice mensal que indica a variação dos preços e serviços no comércio.
                      É considerado, pelo Banco Central, o índice brasileiro oficial da inflação.
                    </p>
                  }
                >
                  <InfoCircleOutlined className="simulation-info" style={{ color: '#00441F' }} />
                </Tooltip>
              </label>
            }
          />
        </div>

        <button
          type='button'
          className="link-style simulator-informations"
          onClick={() => { setVisible(true) }}
        >
          VER MAIS INFORMAÇÕES
        </button>

        <RefinancingButton
          handleClick={onFinish}
          loading={submitLoading}
        />
      </div>
    </Fragment>
  )
}
